import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useState, useRef, useCallback } from 'react';

export interface ConsultaJob {
  id: string;
  user_id: string;
  name: string;
  source_type: 'upload' | 'paste';
  in_file_url?: string;
  in_text_url?: string;
  out_file_url?: string;
  total_inputs: number;
  unique_inputs: number;
  processed: number;
  failed: number;
  status: 'QUEUED' | 'RUNNING' | 'DONE' | 'ERROR' | 'ERROR_PERMANENTE';
  error_message?: string;
  preview?: any[];
  created_at: string;
  started_at?: string;
  finished_at?: string;
  updated_at: string;
  retry_count?: number;
}

export interface JobProgress {
  processed: number;
  failed: number;
  total: number;
}

export function useConsultaOperadora() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<Record<string, JobProgress>>({});
  const [inlineResults, setInlineResults] = useState<Record<string, any[]>>({});
  const activeSubscriptionsRef = useRef<Map<string, () => void>>(new Map());
  const [hasActiveJobs, setHasActiveJobs] = useState(false);
  const [hasLargeActiveJobs, setHasLargeActiveJobs] = useState(false);

  // Fetch jobs with auto-refresh when there are active jobs
  const { data: jobs = [], isLoading, refetch } = useQuery({
    queryKey: ['consulta-operadora-jobs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('consulta_operadora_jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      
      // Update hasActiveJobs state for auto-refresh adaptativo
      const ativos = (data || []).filter(j => j.status === 'RUNNING' || j.status === 'QUEUED');
      const hasRunning = ativos.length > 0;
      const hasLarge = ativos.some(j => (j.unique_inputs || j.total_inputs || 0) > 50_000);
      setHasActiveJobs(hasRunning);
      setHasLargeActiveJobs(hasLarge);
      
      return data as ConsultaJob[];
    },
    // Auto-refresh adaptativo: 8s quando há jobs grandes (>50k), 15s para outros, off sem jobs
    refetchInterval: hasActiveJobs ? (hasLargeActiveJobs ? 8000 : 15000) : false,
  });

  // Subscribe to realtime progress with cleanup management
  const subscribeToProgress = useCallback((jobId: string) => {
    // 🧹 Limpar subscription antiga se existir
    const oldCleanup = activeSubscriptionsRef.current.get(jobId);
    if (oldCleanup) {
      console.log(`🧹 [ConsultaOperadora] Limpando subscription antiga: ${jobId}`);
      oldCleanup();
    }

    const channel = supabase.channel(`consulta_operadora:${jobId}`);
    
    channel
      .on('broadcast', { event: 'progress' }, (payload: any) => {
        setProgress(prev => ({ ...prev, [jobId]: payload.payload }));
      })
      .subscribe();

    // ✅ Armazenar função de cleanup
    const cleanup = () => {
      console.log(`🔌 [ConsultaOperadora] Removendo channel: ${jobId}`);
      supabase.removeChannel(channel);
    };
    
    activeSubscriptionsRef.current.set(jobId, cleanup);
    
    return cleanup;
  }, []);

  // Trigger function
  const triggerFunction = async (jobId: string, opts?: { inline?: boolean }) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Usuário não autenticado');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/consulta-operadora`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ job_id: jobId, inline: opts?.inline ?? false }),
      }
    );

    const json = await response.json();
    
    if (json?.inline_results) {
      setInlineResults(prev => ({ ...prev, [jobId]: json.inline_results }));
    }

    if (!response.ok) {
      throw new Error(json.error || 'Erro ao processar job');
    }

    return json;
  };

  // Create job from upload
  const createJobFromUpload = useMutation({
    mutationFn: async ({ name, file }: { name: string; file: File }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Upload file
      const path = `${user.id}/${Date.now()}_${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('consultas-operadora-in')
        .upload(path, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Create job with path (not URL) - edge function will generate signed URL
      const { data: job, error: jobError } = await supabase
        .from('consulta_operadora_jobs')
        .insert({
          user_id: user.id,
          name,
          source_type: 'upload',
          in_file_url: uploadData.path, // Store path instead of public URL
        })
        .select('*')
        .single();

      if (jobError) throw jobError;

      // Subscribe and trigger
      subscribeToProgress(job.id);
      await triggerFunction(job.id);

      return job;
    },
    onSuccess: () => {
      toast({ title: 'Processamento iniciado', description: 'Arquivo sendo processado...' });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao criar job',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Create job from paste
  const createJobFromPaste = useMutation({
    mutationFn: async ({ name, numbers, blob: blobOverride, totalCount }: { name: string; numbers: string[] | Blob; blob?: Blob; totalCount?: number }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Upload text file
      // Aceita Blob direto (modo lista grande, evita string intermediária de MBs)
      // ou string[] (modo pequeno tradicional)
      let blob: Blob;
      let countNumbers: number;
      if (numbers instanceof Blob) {
        blob = numbers;
        countNumbers = totalCount ?? 0;
      } else if (blobOverride) {
        blob = blobOverride;
        countNumbers = numbers.length;
      } else {
        // Constrói Blob via array de partes (evita criar string gigante intermediária)
        const parts: BlobPart[] = [];
        for (let i = 0; i < numbers.length; i++) {
          parts.push(numbers[i]);
          if (i < numbers.length - 1) parts.push('\n');
        }
        blob = new Blob(parts, { type: 'text/plain' });
        countNumbers = numbers.length;
      }
      const path = `${user.id}/${Date.now()}_${name.replace(/[^\w\-]+/g, '_')}.txt`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('consultas-operadora-text')
        .upload(path, blob, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('consultas-operadora-text')
        .getPublicUrl(uploadData.path);

      // Create job
      const { data: job, error: jobError } = await supabase
        .from('consulta_operadora_jobs')
        .insert({
          user_id: user.id,
          name,
          source_type: 'paste',
          in_text_url: publicUrl,
          total_inputs: countNumbers,
        })
        .select('*')
        .single();

      if (jobError) throw jobError;

      // Subscribe and trigger
      subscribeToProgress(job.id);
      await triggerFunction(job.id, { inline: countNumbers > 0 && countNumbers <= 10 });

      return job;
    },
    onSuccess: () => {
      toast({ title: 'Processamento iniciado', description: 'Números sendo processados...' });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao criar job',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Test connection
  const testConnection = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Usuário não autenticado');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/consulta-operadora-ping`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const json = await response.json();
      
      if (!response.ok || !json.ok) {
        throw new Error(json.error || `Falha: HTTP ${json.responseStatus}`);
      }

      return json;
    },
    onSuccess: () => {
      toast({ 
        title: 'Conexão testada', 
        description: 'API de consulta está funcionando corretamente!' 
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao testar conexão',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete job (com exclusão de arquivos no Storage)
  const deleteJob = useMutation({
    mutationFn: async (jobId: string) => {
      console.log(`🗑️ Iniciando exclusão do job ${jobId}...`);
      
      // Buscar job para pegar paths dos arquivos
      const { data: job, error: fetchError } = await supabase
        .from('consulta_operadora_jobs')
        .select('in_file_url, out_file_url, in_text_url')
        .eq('id', jobId)
        .single();
      
      if (fetchError) {
        console.warn('⚠️ Erro ao buscar job para exclusão:', fetchError);
      }
      
      // Tentar deletar arquivos do Storage (ignorar erros)
      if (job) {
        try {
          // Arquivo de entrada (upload)
          if (job.in_file_url) {
            await supabase.storage.from('consultas-operadora-in').remove([job.in_file_url]);
            console.log('✅ Arquivo de entrada removido');
          }
          // Arquivo de texto (paste)
          if (job.in_text_url) {
            const txtPath = job.in_text_url.split('/').slice(-2).join('/');
            await supabase.storage.from('consultas-operadora-text').remove([txtPath]);
            console.log('✅ Arquivo de texto removido');
          }
          // Pasta de saída (resultado)
          const { data: outFiles } = await supabase.storage
            .from('consultas-operadora-out')
            .list(jobId);
          if (outFiles && outFiles.length > 0) {
            const paths = outFiles.map(f => `${jobId}/${f.name}`);
            await supabase.storage.from('consultas-operadora-out').remove(paths);
            console.log('✅ Arquivos de saída removidos');
          }
        } catch (storageErr) {
          console.warn('⚠️ Erro ao excluir arquivos do Storage:', storageErr);
          // Continuar mesmo com erro no Storage
        }
      }
      
      // Deletar registro do banco
      const { error, count } = await supabase
        .from('consulta_operadora_jobs')
        .delete()
        .eq('id', jobId)
        .select('id');

      if (error) {
        console.error('❌ Erro RLS ao deletar job:', error);
        throw new Error(`Sem permissão para excluir este job. ${error.message}`);
      }
      
      console.log(`✅ Job ${jobId} excluído com sucesso`);
    },
    onSuccess: () => {
      toast({ 
        title: 'Job excluído', 
        description: 'Registro e arquivos removidos com sucesso' 
      });
      refetch();
    },
    onError: (error: any) => {
      console.error('❌ Falha ao excluir job:', error);
      toast({
        title: 'Erro ao excluir',
        description: error.message || 'Verifique se você tem permissão para excluir este job',
        variant: 'destructive',
      });
    },
  });

  // Cancelar job travado
  const cancelJob = useMutation({
    mutationFn: async (jobId: string) => {
      const { error } = await supabase
        .from('consulta_operadora_jobs')
        .update({ 
          status: 'ERROR', 
          error_message: 'Cancelado manualmente pelo usuário',
          finished_at: new Date().toISOString()
        })
        .eq('id', jobId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Job cancelado', description: 'Processamento foi interrompido' });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao cancelar',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Retomar job travado
  const resumeJob = useMutation({
    mutationFn: async (jobId: string) => {
      // Primeiro, resetar status para QUEUED
      const { error: resetError } = await supabase
        .from('consulta_operadora_jobs')
        .update({ 
          status: 'QUEUED', 
          error_message: null,
          started_at: null,
          finished_at: null
        })
        .eq('id', jobId);

      if (resetError) throw resetError;

      // Depois, disparar o processamento
      subscribeToProgress(jobId);
      await triggerFunction(jobId);
    },
    onSuccess: () => {
      toast({ title: 'Job retomado', description: 'Processamento reiniciado' });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao retomar',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Consulta rápida (até 50 números, retorna direto)
  const consultarRapida = useMutation({
    mutationFn: async (numbers: string[]) => {
      if (numbers.length > 50) throw new Error('Máximo de 50 números permitidos na consulta rápida — use Importação em Lote para mais');
      if (numbers.length === 0) throw new Error('Informe ao menos 1 número');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Usuário não autenticado');

      // (27/07) TIMEOUT: sem ele a promessa nunca resolve se a conexão com o
      // fornecedor pendurar, e o campo Operadora Doadora gira eternamente — sem
      // erro, sem mensagem e sem o pedido poder sair. O backend já tenta 3x com
      // 25s cada, então 90s cobre o pior caso legítimo; passou disso, é travamento.
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL ?? 'https://beta.crm-api.primetecia.com.br/api'}/functions/v1/consulta-operadora`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            quick_query: true,
            numbers
          }),
          // 20s: o backend agora resolve a consulta interativa em ~16s no pior caso
          // (2 tentativas de 8s). Passou disso, algo travou e é melhor dizer isso ao
          // consultor do que deixar a bolinha girando.
          signal: AbortSignal.timeout(20_000),
        }
      );

      if (!response.ok) {
        const json = await response.json();
        const error: any = new Error(json.error || 'Erro ao consultar números');
        error.status = response.status;
        throw error;
      }

      const json = await response.json();
      return json.results || [];
    },
    onError: (error: any) => {
      if (error?.status === 402) {
        toast({
          title: 'Sem saldo na API',
          description: '⚠️ A conta da API de consulta de operadora está sem saldo disponível',
          variant: 'destructive',
        });
        return;
      }
      
      toast({
        title: 'Erro na consulta rápida',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // ⚡ Forçar retomada via CRON (atalho enquanto espera o cron de 1 min)
  const forcarRetomada = useMutation({
    mutationFn: async (jobId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Usuário não autenticado');

      // Reseta para RUNNING (se ERROR) e dispara a edge principal direto
      await supabase
        .from('consulta_operadora_jobs')
        .update({
          status: 'RUNNING',
          error_message: null,
          finished_at: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', jobId);

      subscribeToProgress(jobId);
      await triggerFunction(jobId);
    },
    onSuccess: () => {
      toast({
        title: 'Retomada forçada',
        description: 'Processamento reiniciado a partir do checkpoint.',
      });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao forçar retomada',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // ✅ Cleanup global ao desmontar componente
  useEffect(() => {
    return () => {
      console.log('🧹 [ConsultaOperadora] Limpando TODAS as subscriptions ao desmontar');
      activeSubscriptionsRef.current.forEach(cleanup => cleanup());
      activeSubscriptionsRef.current.clear();
    };
  }, []);

  return {
    jobs,
    isLoading,
    progress,
    inlineResults,
    refetch,
    createJobFromUpload,
    createJobFromPaste,
    testConnection,
    deleteJob,
    cancelJob,
    resumeJob,
    consultarRapida,
    forcarRetomada,
    hasLargeActiveJobs,
  };
}
